import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

// Helper to register account in persistent localStorage (avoids circular dep with DataContext)
function persistAccount(account) {
    try {
        const raw = localStorage.getItem('p2g_accounts');
        const accounts = raw ? JSON.parse(raw) : [];
        const index = accounts.findIndex(u => u.email === account.email);
        const accountData = {
            id: account.id || Date.now(),
            name: account.name,
            email: account.email,
            role: account.role === 'Patient / Self-Advocate' ? 'Patient' : account.role,
            patientId: account.patientId, 
            patientName: account.patientName, // Critical for fallback visibility
            status: 'Active',
            added: new Date().toISOString().split('T')[0]
        };

        if (index > -1) {
            accounts[index] = { ...accounts[index], ...accountData };
        } else {
            accounts.push(accountData);
        }
        localStorage.setItem('p2g_accounts', JSON.stringify(accounts));
        return accountData;
    } catch { return null; }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    const login = async (email, password) => {
        await new Promise(r => setTimeout(r, 800));
        const emailLower = email.toLowerCase();

        // 1. Check persistent accounts first (for newly registered users)
        try {
            const raw = localStorage.getItem('p2g_accounts');
            const accounts = raw ? JSON.parse(raw) : [];
            const match = accounts.find(a => a.email.toLowerCase() === emailLower);
            if (match) {
                // HEALING LOGIC: Ensure demo accounts have correct links
                if (emailLower.includes('patient') || emailLower.includes('parent')) {
                    if (!match.patientId || !match.patientName) {
                        let linkedId = 1;
                        let linkedName = 'Alex Johnson';
                        if (emailLower.includes('mia')) { linkedId = 2; linkedName = 'Mia Wong'; }
                        else if (emailLower.includes('leo')) { linkedId = 3; linkedName = 'Leo Smith'; }
                        
                        match.patientId = match.patientId || linkedId;
                        match.patientName = match.patientName || linkedName;
                    }
                }
                setUser(match);
                navigate(match.role === 'Patient' ? '/patient' : '/app');
                return;
            }
        } catch { }

        // 2. Fallback to hardcoded demo accounts
        if (emailLower.includes('patient') || emailLower.includes('parent')) {
            let linkedPatientId = 1;
            let linkedPatientName = 'Alex Johnson';
            let uName = "Alex's Parent";

            if (emailLower.includes('mia')) { linkedPatientId = 2; linkedPatientName = 'Mia Wong'; uName = "Mia's Parent"; }
            else if (emailLower.includes('leo')) { linkedPatientId = 3; linkedPatientName = 'Leo Smith'; uName = "Leo's Parent"; }

            const u = { id: Date.now(), patientId: linkedPatientId, patientName: linkedPatientName, name: uName, role: 'Patient', email };
            setUser(u);
            navigate('/patient');
        } else if (emailLower.includes('admin')) {
            const u = { id: 99, name: 'Admin User', role: 'Admin', email };
            setUser(u);
            navigate('/app');
        } else {
            const u = { id: 1, name: 'Dr. Sarah', role: 'Therapist', email };
            setUser(u);
            navigate('/app');
        }
    };


    const register = async (data) => {
        await new Promise(r => setTimeout(r, 800));
        const role = data.role === 'Patient / Self-Advocate' ? 'Patient' : data.role;
        const u = { id: Date.now(), ...data, role };
        const savedAccount = persistAccount(u);
        setUser(savedAccount || u);
        
        if (role === 'Patient') {
            navigate('/patient');
        } else {
            navigate('/app');
        }
    };

    const logout = () => {
        setUser(null);
        navigate('/');
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
